import { useMemo } from 'react'

import { useFormik } from '@fourthwall/utils/dist/formik'

import { useRequestRefundMutation } from '../../../hooks/useRequestRefundMutation'
import { formatCurrencyFromDecimal, formatCurrencyToDecimal } from '../../../utils'
import { validationSchema } from '../consts'
import type { FormValues } from '../types'
import { CancelRequestType, RefundRequestType } from '../types'

type Props = {
  onSuccess: (cancelRequestType?: CancelRequestType) => void
}

export const useRefundModal = ({ onSuccess }: Props) => {
  const { requestRefundMutation } = useRequestRefundMutation()

  const form = useFormik<FormValues>({
    initialValues: {
      transaction: undefined,
      cancelRequestType: CancelRequestType.Schedule,
      refundRequestType: RefundRequestType.full,
      customAmount: '0',
      cancelSubscription: false,
    },
    validationSchema,
    onSubmit: (values) => {
      if (!values.transaction) return

      const cancelRequestType = values.cancelSubscription ? values.cancelRequestType : undefined

      if (values.refundRequestType === RefundRequestType.custom) {
        const amount = formatCurrencyToDecimal(
          parseFloat(values.customAmount),
          values.transaction.currency.decimalPlaces,
        )

        return requestRefundMutation.mutate(
          [
            {
              amount,
              chargeId: values.transaction.id,
              cancelRequestType,
            },
          ],
          {
            onSuccess: () => onSuccess(cancelRequestType),
          },
        )
      }

      if (
        values.refundRequestType === RefundRequestType.prorated &&
        values.transaction.refundRequest.type === 'Allowed' &&
        values.transaction.refundRequest.options.prorated.type === 'Allowed'
      ) {
        const amount = values.transaction.refundRequest.options.prorated.amount

        return requestRefundMutation.mutate(
          [
            {
              amount,
              chargeId: values.transaction.id,
              cancelRequestType,
            },
          ],
          {
            onSuccess: () => onSuccess(cancelRequestType),
          },
        )
      }

      if (
        values.refundRequestType === RefundRequestType.full &&
        values.transaction.refundRequest.type === 'Allowed' &&
        values.transaction.refundRequest.options.full
      ) {
        const amount = values.transaction.refundRequest.options.full.amount

        return requestRefundMutation.mutate(
          [
            {
              amount,
              chargeId: values.transaction.id,
              cancelRequestType,
            },
          ],
          {
            onSuccess: () => onSuccess(cancelRequestType),
          },
        )
      }
    },
  })

  const refundAmount = useMemo(() => {
    if (form.values.transaction?.refundRequest.type !== 'Allowed') return undefined

    if (
      form.values.refundRequestType === RefundRequestType.full &&
      form.values.transaction.refundRequest.options.full
    )
      return formatCurrencyFromDecimal(
        form.values.transaction.refundRequest.options.full.amount,
        form.values.transaction.currency.decimalPlaces,
      )

    if (
      form.values.refundRequestType === RefundRequestType.prorated &&
      form.values.transaction.refundRequest.options.prorated &&
      form.values.transaction.refundRequest.options.prorated.type === 'Allowed'
    )
      return formatCurrencyFromDecimal(
        form.values.transaction.refundRequest.options.prorated.amount,
        form.values.transaction.currency.decimalPlaces,
      )

    if (form.values.refundRequestType === RefundRequestType.custom)
      return parseFloat(form.values.customAmount)
  }, [form])

  return {
    form,
    refundAmount,
    isLoading: requestRefundMutation.isLoading,
  }
}
